import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "@/core/auth/services/authService";
import { useAuth } from "@/core/auth/hooks/useAuth";
import { appointmentKeys } from "@/features/appointments/hooks/appointmentQueries";
import { useCurrentBusiness } from "@/features/business/hooks/useCurrentBusiness";
import { Button } from "@/shared/components/shadcn/Button";
import { Checkbox } from "@/shared/components/shadcn/Checkbox";
import { Input } from "@/shared/components/shadcn/Input";
import { ConfirmationDialog } from "@/shared/components/ui/ConfirmationDialog";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { Page, PageContent } from "@/shared/components/ui/Page";
import { PageHeader } from "@/shared/components/ui/PageHeader";
import { Section } from "@/shared/components/ui/Section";
import { useAppNavigation } from "@/shared/hooks/useAppNavigation";
import { useWorkspaceOperation } from "@/shared/hooks/useWorkspaceOperation";
import { CustomerPaymentFlow } from "../components/CustomerPaymentFlow";
import { PaymentState } from "../components/PaymentState";
import { paymentKeys, usePayment, usePaymentMutations } from "../hooks/paymentQueries";
import { paymentApiService } from "../services/paymentApiService";
import { fromPaymentSessionDtoToPaymentSession, isPaymentTerminal, type PaymentSession } from "../types/paymentTypes";

function PaymentContent({ appointmentId, paymentId, businessId, userId }: {
  appointmentId: string; paymentId: string; businessId: string; userId: string;
}) {
  const query = usePayment(appointmentId, paymentId);
  const { cancel, forceVoid } = usePaymentMutations(appointmentId);
  const capture = useWorkspaceOperation();
  const { goToAppointmentDetails, goToAppointmentCharge } = useAppNavigation();
  const cache = useQueryClient();
  const [session, setSession] = useState<PaymentSession>();
  const [mode, setMode] = useState<"device" | "link">("link");
  const [sessionKey, setSessionKey] = useState(() => crypto.randomUUID());
  const [cancelKey] = useState(() => crypto.randomUUID());
  const [voidKey] = useState(() => crypto.randomUUID());
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState("");
  const [cancelOpen, setCancelOpen] = useState(false);
  const [chargeAgain, setChargeAgain] = useState(false);
  const [cancelResult, setCancelResult] = useState(false);
  const [voidOpen, setVoidOpen] = useState(false);
  const [voidAcknowledged, setVoidAcknowledged] = useState(false);
  const pending = useRef<AbortController | null>(null);
  const canceling = useRef(false);
  const voiding = useRef(false);
  useEffect(() => () => pending.current?.abort(), []);
  function openCancel(thenCharge: boolean) {
    cancel.reset();
    setChargeAgain(thenCharge);
    setCancelOpen(true);
  }
  async function handoff(nextMode: "device" | "link") {
    if (pending.current || cancel.isPending) return;
    if (session && session.expiresAt.getTime() > Date.now()) { setMode(nextMode); return; }
    const current = new AbortController();
    pending.current = current;
    const isCurrent = capture();
    setBusy(true);
    setError("");
    try {
      const result = fromPaymentSessionDtoToPaymentSession(
        await paymentApiService.createSession(businessId, appointmentId, paymentId, sessionKey, current.signal));
      if (isCurrent() && !current.signal.aborted) { setSession(result); setMode(nextMode); setCopied(false); }
    } catch {
      if (isCurrent() && !current.signal.aborted) setError("Couldn't prepare a customer link. Refresh payment status and retry this same request.");
    } finally {
      pending.current = null;
      if (isCurrent()) setBusy(false);
    }
  }
  async function copyLink() {
    if (!session) return;
    const isCurrent = capture();
    setError("");
    try {
      await navigator.clipboard.writeText(session.url);
      if (isCurrent()) setCopied(true);
    } catch {
      if (isCurrent()) setError("Couldn't copy the link. Select and copy it below.");
    }
  }
  function done() {
    void cache.invalidateQueries({ queryKey: paymentKeys.appointment(businessId, appointmentId, userId), refetchType: "none" });
    void cache.invalidateQueries({ queryKey: appointmentKeys.detail(appointmentId, businessId, userId), refetchType: "none" });
    goToAppointmentDetails(appointmentId, true);
  }
  if (query.isPending) return <LoadingSpinner message="Loading payment..." />;
  if (query.error) return <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />;
  const payment = query.data;
  if (!payment) return <ErrorState message="Payment not found." />;
  const terminal = isPaymentTerminal(payment);
  const resolving = busy || cancel.isPending || forceVoid.isPending;
  const canVoid = payment.cancelRequested && ["preparing", "reconciliation_required"].includes(payment.status);
  const canHandoff = ["requires_payment_method", "requires_confirmation", "requires_action"].includes(payment.status);
  return <div className="space-y-6">
    <Section title="Payment" card><PaymentState payment={payment} />
      <Button variant="quiet" disabled={query.isFetching || cancel.isPending} onClick={() => void query.refetch()}>Refresh payment status</Button>
    </Section>
    {!terminal && canHandoff && <Section title="Customer payment" card>
      <p className="text-sm text-muted-foreground">The customer completes payment or bank verification. Both options use this same payment request.</p>
      <div className="flex flex-wrap gap-2">
        <Button disabled={busy || cancel.isPending} onClick={() => void handoff("device")}>Pay on this device</Button>
        <Button variant="outline" disabled={busy || cancel.isPending} onClick={() => void handoff("link")}>Create payment link</Button>
      </div>
      {busy && <LoadingSpinner message="Preparing secure customer payment..." />}
      {session && !cancel.isPending && (mode === "device" && authService.kind !== "host" ?
        <CustomerPaymentFlow key={session.id} token={session.token} onDone={done} /> :
        <div className="space-y-3">
          <p className="text-sm">{mode === "device" ? "Open the secure form in your browser and let the customer complete it." :
            "Share this private link only with this customer. They do not need a Froppio account."}</p>
          <label className="block space-y-2 text-sm">Secure payment link
            <Input readOnly value={session.url} onFocus={event => event.currentTarget.select()} />
          </label>
          <p className="text-xs text-muted-foreground">Expires {session.expiresAt.toLocaleString()}. Expiry does not cancel this payment.</p>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => void copyLink()}>{copied ? "Link copied" : "Copy payment link"}</Button>
            <Button asChild><a href={session.url} target="_blank" rel="noopener noreferrer">Open secure form</a></Button>
            <Button variant="quiet" disabled={busy} onClick={() => {
              setSession(undefined); setSessionKey(crypto.randomUUID()); setCopied(false); setError("");
            }}>Prepare a fresh link</Button>
          </div>
          <p role="status" className="sr-only">{copied ? "Payment link copied." : ""}</p>
        </div>)}
      {error && <ErrorState message={error} />}
    </Section>}
    {!terminal && <Section title="Resolve payment" card>
      <p className="text-sm text-muted-foreground">Canceling stops an unpaid request only after Stripe confirms cancellation. A payment already in progress may still succeed.</p>
      <div className="flex flex-wrap gap-2">
        <Button variant="outline" disabled={resolving}
          onClick={() => openCancel(false)}>{payment.status === "canceling" || payment.cancelRequested ? "Retry cancellation" : "Cancel payment request"}</Button>
        <Button variant="outline" disabled={resolving} onClick={() => openCancel(true)}>Cancel & charge again</Button>
      </div>
      {cancel.error && <ErrorState message="Cancellation is not confirmed. Refresh this payment before taking another payment." />}
      {canVoid && <div className="space-y-2">
        <p className="text-sm text-muted-foreground">If Stripe can't confirm this request, you can void it here and start a new payment. Check your Stripe dashboard first.</p>
        <Button variant="outline" disabled={resolving}
          onClick={() => { forceVoid.reset(); setVoidAcknowledged(false); setVoidOpen(true); }}>Void without Stripe confirmation</Button>
      </div>}
      {cancelResult && <ErrorState message="Stripe hasn't confirmed cancellation yet. Refresh payment status before charging again." />}
    </Section>}
    {terminal && (payment.status === "canceled" || payment.status === "voided") &&
      <Button variant="outline" onClick={() => goToAppointmentCharge(appointmentId, "finish")}>Charge again</Button>}
    <Button variant={terminal ? "default" : "outline"} onClick={done}>{terminal ? "Done" : "Return to appointment"}</Button>
    <ConfirmationDialog open={cancelOpen} onOpenChange={setCancelOpen}
      title={chargeAgain ? "Cancel and charge again?" : "Cancel this payment request?"}
      description={chargeAgain ? "This is not a refund. After Stripe confirms cancellation, you can choose a new amount or card." :
        "This is not a refund. Stripe must confirm cancellation before another payment can be started."}
      confirmLabel="Cancel request" dismissLabel="Keep payment" destructive pending={cancel.isPending}
      error={cancel.error ? "Couldn't verify cancellation. Refresh payment status before starting another payment." : undefined}
      onConfirm={() => {
        if (canceling.current) return;
        canceling.current = true;
        const isCurrent = capture();
        const thenCharge = chargeAgain;
        setSession(undefined);
        cancel.mutate({ paymentId, idempotencyKey: cancelKey }, {
          onSuccess: result => {
            if (!isCurrent()) return;
            setCancelOpen(false);
            if (thenCharge && isPaymentTerminal(result) && result.status !== "succeeded") {
              goToAppointmentCharge(appointmentId, "finish");
              return;
            }
            setCancelResult(thenCharge && !isPaymentTerminal(result));
            void query.refetch();
          },
          onError: () => { if (isCurrent()) void query.refetch(); },
          onSettled: () => { canceling.current = false; },
        });
      }} />
    <ConfirmationDialog open={voidOpen} onOpenChange={open => { if (!forceVoid.isPending) setVoidOpen(open); }}
      title="Void without Stripe confirmation?" confirmLabel="Void request" dismissLabel="Keep payment" destructive
      pending={forceVoid.isPending} confirmDisabled={!voidAcknowledged}
      error={forceVoid.error ? forceVoid.error.message : undefined}
      description={<>
        Froppio retries cancellation with Stripe first. If Stripe still can't confirm the outcome, this request is voided so you can charge again.
        <label className="mt-3 flex items-start gap-2">
          <Checkbox checked={voidAcknowledged} disabled={forceVoid.isPending} onCheckedChange={value => setVoidAcknowledged(value === true)} />
          <span>I checked Stripe. If this payment still succeeds, the customer may be charged twice and I will refund it.</span>
        </label>
      </>}
      onConfirm={() => {
        if (voiding.current || !voidAcknowledged) return;
        voiding.current = true;
        const isCurrent = capture();
        setSession(undefined);
        forceVoid.mutate({ paymentId, idempotencyKey: voidKey }, {
          onSuccess: () => { if (isCurrent()) { setVoidOpen(false); setCancelResult(false); void query.refetch(); } },
          onError: () => { if (isCurrent()) void query.refetch(); },
          onSettled: () => { voiding.current = false; },
        });
      }} />
  </div>;
}
export function AppointmentPaymentPage() {
  const { appointmentId = "", paymentId = "" } = useParams();
  const { business, businessId } = useCurrentBusiness();
  const { user } = useAuth();
  return <Page>
    <meta name="referrer" content="no-referrer" />
    <PageHeader title="Appointment payment" />
    <PageContent><div className="mx-auto max-w-2xl">
      {businessId && user && business?.ownerId === user.id ?
        <PaymentContent key={`${user.id}:${businessId}:${appointmentId}:${paymentId}`}
          appointmentId={appointmentId} paymentId={paymentId} businessId={businessId} userId={user.id} /> :
        <ErrorState message="Only the business owner can manage this payment." />}
    </div></PageContent>
  </Page>;
}
